<div class="flex flex-col w-full" skeletonScreen [showSkeleton]="isLoading">
    <div class="z-7 bg-white pb-lg flex flex-col gap-lg" #filtersStickyContainer>
        <!-- Filter by Employee -->
        <div class="flex flex-col gap-md" skeletonData>
            <label hlmLabel for="employee-select" class="text-sm font-semibold"> Filter by Employee </label>

            <div class="relative w-full">
                <hlm-select
                    id="employee-select"
                    class="w-full"
                    [value]="selectedEmployee ? selectedEmployee.id : undefined"
                    (valueChange)="onEmployeeChange($event)"
                    data-testid="employee-select"
                >
                    <hlm-select-trigger class="w-full">
                        <hlm-select-value></hlm-select-value>
                    </hlm-select-trigger>

                    <hlm-select-content>
                        <hlm-option *ngFor="let employee of employees" [value]="employee.id">
                            {{ employee.name }}
                        </hlm-option>
                    </hlm-select-content>
                </hlm-select>

                <!-- Spinner (positioned over the right icon area) -->
                <div *ngIf="isEmployeeLoading" class="absolute inset-y-0 right-4 flex items-center">
                    <hlm-spinner class="size-4 m-auto bg-white"></hlm-spinner>
                </div>
            </div>
        </div>

        <!-- Filter by Category -->
        <div class="flex flex-col gap-md select-none" skeletonData>
            @if (selectedEmployee && selectedEmployee.id === '0') {
                <label hlmLabel for="category-select" class="text-sm font-semibold"> Filter by Category </label>
            } @else if (selectedEmployee && selectedEmployee.id !== '0' && selectedServices.length > 0 && employees.length > 1) {
                <div hlmAlert class="!block !flex !flex-row !grid-cols-none items-start gap-2 p-3 rounded-md bg-secondary-100 mb-2 mt-1">
                    <ng-icon
                        hlm
                        hlmAlertIcon
                        name="lucideInfo"
                        size="20px"
                        class="mt-[2px] shrink-0"
                        [style.color]="'var(--color-secondary-500, #746D83)'"
                    />

                    <p class="text-text-primary font-normal text-xs leading-normal">
                        All services must be booked with the same employee. Employees and categories are filtered based on your first
                        service.
                    </p>
                </div>
            }
            <div class="flex gap-2 overflow-x-auto scrollbar-hide category-scroll" horizontalScroll #horScrollDirective="horizontalScroll">
                <span
                    *ngFor="let category of categoryList"
                    hlmbadge
                    variant="chip"
                    data-testid="category-button"
                    class="cursor-pointer flex-shrink-0 [&_fa-icon]:pointer-events-none [&_fa-icon]:size-5.5 gap-xs h-[32px] inline-flex items-center justify-center overflow-hidden px-lg transition-colors whitespace-nowrap"
                    [ngClass]="{
                        'bg-primary-base text-text-on-primary rounded-full hover:bg-primary-base/70': selectedCategory === category,
                        'bg-secondary-100 text-text-primary rounded-sm hover:bg-primary-light': selectedCategory !== category,
                    }"
                    (click)="onCategoryChange({ value: category })"
                >
                    {{ limitMaxLength(category) }}
                    <span
                        *ngIf="showSelectedCategoryServicesCount(category)"
                        class="ml-2 inline-flex items-center justify-center text-xs font-semibold rounded-full px-2 py-0.5"
                        [ngClass]="{
                            'bg-primary-dark text-white': selectedCategory !== category,
                            'bg-primary-light text-black': selectedCategory === category,
                        }"
                    >
                        {{ countSelectedServices(category) }}
                    </span>
                </span>
            </div>
        </div>
    </div>

    <!-- Scrollable Services -->
    <div class="grow" skeletonTable [skeletonTableRowAmount]="5" [placeholderHeight]="'4rem'">
        <div class="flex flex-col gap-2xl">
            <div *ngFor="let group of groupFilteredServiceList">
                <label hlmLabel class="text-lg font-semibold block mb-2">
                    {{ group.key }}
                </label>
                <section
                    hlmCard
                    *ngFor="let service of group.services"
                    class="border mt-2 rounded-xl"
                    [ngClass]="{ 'service-selected-shadow': isServiceSelected(service) }"
                    data-testid="service-item"
                >
                    <div hlmCardHeader class="flex flex-col gap-1 items-start text-left">
                        <div class="flex justify-between items-center w-full">
                            <div class="flex flex-col">
                                <label hlmLabel hlmCardTitle class="font-semibold text-md">{{ service.name }}</label>
                                <div class="flex h-4 items-center space-x-2 mt-2">
                                    <!-- Duration -->
                                    <div *ngIf="isServicePriceAndDurationFeatureActive">
                                        <label hlmLabel class="text-sm text-text-secondary">
                                            <span *ngIf="getServiceDurationPrefix(service)">{{ getServiceDurationPrefix(service) }} </span
                                            >{{ getServiceDurationDisplay(service) | duration }}
                                        </label>
                                    </div>
                                    <div
                                        class="w-px h-5 bg-[var(--color-base-border,#D8D6DC)]"
                                        *ngIf="isServicePriceAndDurationFeatureActive && shouldShowServicePrice"
                                    ></div>
                                    <!-- Price -->
                                    <div *ngIf="shouldShowServicePrice">
                                        <label hlmLabel class="text-sm font-semibold">
                                            <span *ngIf="getServicePriceDisplay(service)">{{ getServicePriceDisplay(service) }} </span
                                            >{{ getServicePrice(service) | dsbCurrency: currencyCode : 'symbol' : '1.0-2' }}</label
                                        >
                                    </div>
                                </div>
                            </div>

                            <div hlmCardAction class="flex items-center">
                                <button
                                    hlmBtn
                                    size="icon"
                                    variant="default"
                                    (click)="toggleServiceSelection(service)"
                                    [attr.aria-label]="isServiceSelected(service) ? 'Remove ' + service.name : 'Add ' + service.name"
                                    [ngStyle]="isServiceSelected(service) ? { 'border-radius': '50%' } : null"
                                    [ngClass]="{
                                        'inline-flex items-center justify-center shadow-xs transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/50 [&_ng-icon]:pointer-events-none [&_ng-icon]:shrink-0': true,
                                        'bg-primary-dark hover:bg-primary-dark/80': !isServiceSelected(service),
                                        'bg-primary-light hover:bg-primary-light text-text-primary': isServiceSelected(service),
                                    }"
                                    data-testid="toggle-service-btn"
                                >
                                    <ng-icon hlm [name]="isServiceSelected(service) ? 'lucideCheck' : 'lucidePlus'"></ng-icon>
                                </button>
                            </div>
                        </div>

                        @if (service.depositRequired && ((service.depositPercentage ?? 0) > 0 || (service.depositFixedAmount ?? 0) > 0)) {
                            <span hlmBadge variant="warning" size="sm" class="mt-2">
                                <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 16 16" fill="none">
                                    <path
                                        d="M13.3333 2.66663H2.66665C1.92665 2.66663 1.33998 3.25996 1.33998 3.99996L1.33331 12C1.33331 12.74 1.92665 13.3333 2.66665 13.3333H13.3333C14.0733 13.3333 14.6666 12.74 14.6666 12V3.99996C14.6666 3.25996 14.0733 2.66663 13.3333 2.66663ZM13.3333 12H2.66665V7.99996H13.3333V12ZM13.3333 5.33329H2.66665V3.99996H13.3333V5.33329Z"
                                        fill="currentColor"
                                    />
                                </svg>
                                @if ((service.depositPercentage ?? 0) > 0) {
                                    {{ service.depositPercentage }}% Deposit required to book
                                } @else {
                                    ${{ service.depositFixedAmount }} Deposit required to book
                                }
                            </span>
                        }

                        <!-- Description -->
                        <p *ngIf="service.description" class="text-sm text-secondary-600 mt-1">
                            {{ service.description }}
                        </p>
                    </div>
                </section>
            </div>
        </div>
    </div>
</div>

<!-- Employee Selection Sheet -->
<hlm-sheet #employeeSelection side="bottom" [closeOnBackdropClick]="false" [closeOnOutsidePointerEvents]="false">
    <hlm-sheet-content *brnSheetContent="let ctx" class="!px-3 flex flex-col">
        <hlm-sheet-header class="!px-0 !py-3 !pb-0">
            <div class="flex items-center justify-between">
                <label hlmLabel class="text-sm font-semibold">
                    {{ selectedServices[0]?.name }}
                </label>

                <ng-icon
                    hlm
                    name="lucideX"
                    (click)="onCancelEmployeeSelectionSheet()"
                    class="w-5 h-5 cursor-pointer text-gray-500 hover:text-gray-700"
                ></ng-icon>
            </div>
        </hlm-sheet-header>

        <!-- Scrollable Employee List -->
        <div class="flex-1 overflow-y-auto">
            <app-select-employee
                [employeeList]="employees"
                [services]="selectedServices"
                (assignSelectedEmployee)="assignSelectedEmployee($event)"
                (isValidEmployeeAndService)="isValidEmployeeAndService = $event"
            ></app-select-employee>
        </div>

        <hlm-sheet-footer class="text-center !px-0 pt-0">
            <button hlmBtn type="button" variant="outline" class="text-md" (click)="onConfirm()">Confirm & Add Another Service</button>
            <button
                hlmBtn
                type="button"
                variant="default"
                class="text-md"
                (click)="onChooseDateTime()"
                [disabled]="!isValidEmployeeAndService"
            >
                Choose Date & Time
            </button>
        </hlm-sheet-footer>
    </hlm-sheet-content>
</hlm-sheet>
